import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  FileText,
  Search,
  GitCompare,
  ListChecks,
  CreditCard,
  CheckCircle2,
  MessageSquare,
  Shield,
  Users,
  Award,
  Calendar,
  FileCheck,
  TrendingUp,
  Settings,
  BarChart3,
  ArrowRight,
  Target,
  Sparkles,
} from "lucide-react";

const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Radius",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "description": "Complete B2B procurement platform with AI specification builder, RFQ management, vendor discovery, and secure payments.",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  }
};

export default function Features() {
  const featureCategories = [
    {
      title: "AI Specification Assistant",
      description: "Build professional specifications through guided conversation—no forms required",
      features: [
        { icon: MessageSquare, title: "Natural Language Input", desc: "Describe your project in plain English. Our AI understands what you need." },
        { icon: Sparkles, title: "Guided Conversation", desc: "AI asks smart follow-up questions to capture all critical requirements." },
        { icon: FileCheck, title: "Automatic Structuring", desc: "Responses are automatically formatted into professional industry-standard specifications." },
        { icon: CheckCircle2, title: "Review & Refine", desc: "Review the extracted spec, make edits, and finalize before publishing your RFQ." },
      ]
    },
    {
      title: "RFQ Management",
      description: "Create and manage detailed requests for quotation",
      features: [
        { icon: FileText, title: "Custom Specifications", desc: "Build detailed specs with custom fields and templates or upload your own spec document." },
        { icon: Target, title: "Vendor Criteria", desc: "Vendors can see they are a good match before bidding" },
        { icon: Users, title: "Supports Complexity", desc: "Specify multiple quantity options, multiple delivery locations, and proof/sample requirements" },
        { icon: Search, title: "Public or Private", desc: "Choose to publish publicly or invite specific vendors" },
      ]
    },
    {
      title: "Vendor Discovery & Bidding",
      description: "Discover vendors and manage the bidding process",
      features: [
        { icon: Search, title: "Advanced Search", desc: "Filter by capabilities, certifications, and location" },
        { icon: Award, title: "Verified Profiles", desc: "Review verified credentials and vendor-submitted work history" },
        { icon: GitCompare, title: "Bid Comparison", desc: "Compare bids side-by-side with line-item detail" },
        { icon: FileCheck, title: "Detailed Proposals", desc: "Vendors submit comprehensive bids with attachments" },
      ]
    },
    {
      title: "Project Management",
      description: "Track and manage projects from award to completion",
      features: [
        { icon: ListChecks, title: "Milestone Tracking", desc: "Break projects into manageable milestones" },
        { icon: Calendar, title: "Deadline Management", desc: "Automated reminders and deadline tracking" },
        { icon: MessageSquare, title: "Built-in Messaging", desc: "Communicate directly with vendors or clients" },
        { icon: CheckCircle2, title: "Progress Monitoring", desc: "Real-time visibility into project status" },
      ]
    },
    {
      title: "Payment & Security",
      description: "Secure payment processing with buyer and vendor protection",
      features: [
        { icon: CreditCard, title: "Stripe Integration", desc: "Secure payment processing with Stripe Connect" },
        { icon: Shield, title: "Purchase Protection", desc: "Funds held securely until milestone completion" },
        { icon: DollarSign, title: "Payment Options", desc: "Payment options that work with your existing processes." },
        { icon: FileCheck, title: "Delivery Verification", desc: "Review proof of delivery before releasing funds" },
      ]
    },
    {
      title: "Reviews & Reputation",
      description: "Build trust through verified reviews and platform performance data",
      features: [
        { icon: Star, title: "Verified Reviews", desc: "Only clients who completed transactions can leave reviews" },
        { icon: TrendingUp, title: "Platform Ratings", desc: "Build credibility with successful projects and on-time delivery" },
        { icon: Award, title: "Verified Credentials", desc: "Showcase verified certifications and enrollment status" },
        { icon: BarChart3, title: "Performance Metrics", desc: "Track on-time delivery and client satisfaction rates" },
      ]
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Platform Features - Radius"
        description="Explore Radius features: AI specification builder, RFQ management, vendor discovery, bid comparison, milestone tracking, and secure payments."
        canonical="/features"
        structuredData={softwareApplicationSchema}
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            Complete <span className="text-primary">Procurement Platform</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to manage B2B procurement from RFQ creation to final payment. All in one powerful platform.
          </p>
          <Button size="lg" asChild>
            <Link to="/auth/signup">
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          </div>
        </div>
      </section>

      {/* Feature Categories */}
      {featureCategories.map((category, index) => (
        <section key={category.title} className={index % 2 === 1 ? "bg-muted/50 py-20" : "py-20"}>
          <div className="container">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-3xl font-bold">{category.title}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {category.description}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {category.features.map((feature) => (
                <div key={feature.title} className="p-6 rounded-lg border bg-background">
                  <feature.icon className="h-8 w-8 text-primary mb-4" />
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Additional Features */}
      <section className="container py-20">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl font-bold">And Much More...</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div className="p-4 rounded-lg border">
            <h3 className="font-semibold mb-2">Document Management</h3>
            <p className="text-sm text-muted-foreground">Upload and organize RFQ attachments, bids, and delivery proof</p>
          </div>
          <div className="p-4 rounded-lg border">
            <h3 className="font-semibold mb-2">Shipment Tracking</h3>
            <p className="text-sm text-muted-foreground">Track multiple shipments with carrier integration</p>
          </div>
          <div className="p-4 rounded-lg border">
            <h3 className="font-semibold mb-2">Template Library</h3>
            <p className="text-sm text-muted-foreground">Reuse RFQ templates and delivery proof templates</p>
          </div>
          <div className="p-4 rounded-lg border">
            <h3 className="font-semibold mb-2">Address Management</h3>
            <p className="text-sm text-muted-foreground">Save and manage multiple delivery addresses</p>
          </div>
          <div className="p-4 rounded-lg border">
            <h3 className="font-semibold mb-2">Project Grouping</h3>
            <p className="text-sm text-muted-foreground">Organize related jobs into projects for better tracking</p>
          </div>
          <div className="p-4 rounded-lg border">
            <h3 className="font-semibold mb-2">Notification System</h3>
            <p className="text-sm text-muted-foreground">Real-time alerts for bids, messages, milestones, and payments</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-y bg-primary/5 py-20">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-bold">Ready to Experience the Complete Platform?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            See how Radius can transform your procurement process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=buyer">
                Sign Up as Buyer
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/auth/signup?type=vendor">
                Sign Up as Vendor
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

const Star = TrendingUp;
const DollarSign = CreditCard;
